// Which sidebar entries a user may see.
//
// Lives apart from OrgAdminLayout because that file's NAV_ITEMS carry JSX icons
// and can't be exercised outside a renderer, whereas this rule is pure and is
// the part worth getting right. Route guards, when they land, share it too.
//
// An item is gated by its `permission` — a `nav.*` key from the auth-service
// catalog (auth-service/src/data/permissionCatalog.js). Items without one
// (dividers, the external website link, the "Coming soon" placeholders) are
// never hidden.
//
// ⚠️ THE LIST IS NOW TWO LEVELS DEEP. A top-level entry is either an ordinary
// item or a `{ section, items: [...] }` group. A section carries no permission
// of its own and is never gated directly — it is shown when anything inside it
// survives the filter and dropped when nothing does. Giving a section its own
// key would be a second switch for a decision its children already make, and
// both drift directions are bad: a group that opens onto nothing, or one that
// hides entries the user is entitled to.

/** Is this entry a collapsible group rather than a link? */
export const isSection = (item) => Boolean(item && item.section && Array.isArray(item.items));

/**
 * Every routed entry in the list, however deeply it is nested.
 *
 * ⚠️ THREE LEVELS EXIST: section → item → item.children (Tasks and Approvals).
 * Anything walking the nav for routes or badges must go all the way down, or a
 * sub-view silently stops being found — which is how "Deleted Tasks" would lose
 * its route guard while still rendering in the menu.
 */
export function flattenNavItems(items = []) {
  return items.flatMap((entry) => {
    const own = isSection(entry) ? entry.items : [entry];
    return own.flatMap((item) => (item.children ? [item, ...item.children] : [item]));
  });
}

/**
 * @param {Array<object>} items   NAV_ITEMS-shaped list; `permission` optional.
 * @param {object|null} user      Auth user; reads `permissions` and `isSuperAdmin`.
 * @param {object} [options]
 * @param {boolean} [options.strict=false]  Require an explicit grant per item.
 */
export function visibleNavItems(items, user, { strict = false } = {}) {
  const permissions = user?.permissions || [];
  const configured = permissions.some((p) => typeof p === 'string' && p.startsWith('nav.'));

  // Super Admin is never gated — they must always be able to reach the
  // Permissions screen to undo a bad grant.
  if (user?.isSuperAdmin) return items;

  // Non-strict callers fail *open* when the user holds no `nav.*` key at all:
  // that means "not configured yet", not "denied everything". It covers a
  // database that hasn't run `npm run migrate:permissions`, and a custom role
  // nobody has curated yet — in both cases an empty sidebar would strand the
  // user with no route back to fix it. This is right for the org-admin cockpit,
  // whose owners could already see every entry.
  //
  // `strict` is for the opposite situation: entries the user has *never* had,
  // which an admin is opting them into one at a time. Failing open there would
  // hand every employee the entire firm cockpit the moment the catalog ships —
  // so no grant means no entry, full stop.
  if (!strict && !configured) return items;

  const permitted = (item) => !item.permission || permissions.includes(item.permission);

  const allowed = items
    .map((entry) => {
      if (!isSection(entry)) return permitted(entry) ? entry : null;
      // ⚠️ A SECTION IS KEPT ONLY IF SOMETHING INSIDE IT SURVIVES. An empty
      // group renders as a chevron that opens onto nothing, which reads as a
      // bug — the same reason the old "Coming soon" group was deleted outright
      // rather than left behind hollow.
      const kept = entry.items.filter(permitted);
      return kept.length ? { ...entry, items: kept } : null;
    })
    .filter(Boolean);

  // Hiding entries strands the dividers that separated them — leaving a
  // doubled rule, or one floating at the very top or bottom of the nav.
  return allowed.filter((item, i, list) => {
    if (!item.divider) return true;
    if (i > 0 && list[i - 1].divider) return false;
    const hasBefore = list.slice(0, i).some((x) => !x.divider);
    const hasAfter = list.slice(i + 1).some((x) => !x.divider);
    return hasBefore && hasAfter;
  });
}

export default visibleNavItems;
